import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'popular' | 'latest'>('popular');

  const popularComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8);
  const displayComics = activeTab === 'popular' ? popularComics : latestComics;

  const hotComics = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 6);
  const newComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 6);

  return (
    <>
      <SEO
        title="韩漫网站-好看的韩国漫画-漫画韩漫网址-韩漫免费阅读漫画"
        description="韩漫网站提供好看韩国漫画免费阅读，精选优质韩漫作品和流行题材，涵盖浪漫爱情、奇幻冒险、都市生活等多种分类。阅读体验舒适且无限制，韩漫网址带给您无限娱乐享受。"
        keywords="韩漫网站,好看韩国漫画,免费阅读,漫画网址,韩漫资源,韩国漫画,在线漫画"
        canonicalUrl="https://www.manhwa-site.com/"
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20a%20Korean%20manhwa%20reading%20scene%20with%20floating%20comic%20panels%20and%20pages%20in%20soft%20golden%20evening%20light%2C%20cozy%20reading%20atmosphere%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20delicate%20line%20art%20aesthetic%2C%20abstract%20decorative%20elements%2C%20clean%20minimal%20composition%20with%20ample%20negative%20space&width=1600&height=900&seq=hero-korean-manhwa-01&orientation=landscape"
            alt="韩漫网站 - 好看的韩国漫画免费阅读"
            title="韩漫网站 - 韩国漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading leading-tight">
              发现好看的<strong>韩国漫画</strong>
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
              精选优质<strong>韩漫</strong>作品，涵盖浪漫爱情、奇幻冒险、都市生活等多种<strong>漫画</strong>分类，带您进入精彩的<strong>韩漫</strong>世界
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/romance"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14" aria-labelledby="categories-title">
          <div className="text-center mb-10">
            <h2 id="categories-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">漫画分类</h2>
            <p className="mt-2 text-sm text-foreground-500">探索您喜欢的韩国漫画类型</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-50 hover:bg-background-100 border border-background-200/70 transition-all duration-300 cursor-pointer"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-500 transition-colors duration-300">
                  <i className={`${cat.icon} text-xl text-accent-600 group-hover:text-background-50 transition-colors duration-300`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">{cat.name}</span>
                <span className="text-xs text-foreground-400 text-center line-clamp-2">{cat.description}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-background-100/60 py-14" aria-labelledby="recommend-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
              <div>
                <h2 id="recommend-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">精选推荐</h2>
                <p className="mt-1 text-sm text-foreground-500">编辑精选优质韩国漫画</p>
              </div>
              <div className="flex items-center bg-background-200/60 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('popular')}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === 'popular' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  热门排行
                </button>
                <button
                  onClick={() => setActiveTab('latest')}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === 'latest' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  最新更新
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {displayComics.map((comic, idx) => (
                <div key={comic.id} className="relative">
                  {activeTab === 'popular' && (
                    <span className="absolute -top-1 -left-1 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                      {idx + 1}
                    </span>
                  )}
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14" aria-labelledby="hot-title">
          <div className="text-center mb-10">
            <h2 id="hot-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">高分好评</h2>
            <p className="mt-2 text-sm text-foreground-500">读者评分最高的韩国漫画作品</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {hotComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <section className="bg-background-100/60 py-14" aria-labelledby="new-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 id="new-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">最近更新</h2>
                <p className="mt-1 text-sm text-foreground-500">刚刚更新的韩国漫画章节</p>
              </div>
              <Link to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14" aria-labelledby="faq-title">
          <div className="text-center mb-10">
            <h2 id="faq-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading">常见问题</h2>
            <p className="mt-2 text-sm text-foreground-500">关于韩漫网站的常见疑问解答</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              { q: '韩漫网站上的漫画都是免费的吗？', a: '是的，我们网站上的所有韩国漫画都可以免费阅读，无需付费订阅。我们致力于为广大漫画爱好者提供优质的免费韩漫资源。' },
              { q: '如何在韩漫网站上找到好看的韩国漫画？', a: '您可以通过首页的分类导航浏览不同题材的韩漫，也可以使用搜索功能查找特定作品。热门排行和高分好评板块也能帮助您发现优质漫画。' },
              { q: '韩漫网站的漫画更新频率如何？', a: '我们会及时跟进韩国漫画的最新更新，热门连载作品通常每周都会更新新的章节，确保读者能够第一时间阅读到最新内容。' },
              { q: '韩漫网站支持手机阅读吗？', a: '当然支持！我们的网站采用响应式设计，无论在电脑、平板还是手机上都能获得舒适流畅的阅读体验。' },
            ].map((faq, i) => (
              <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors duration-200">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': '韩漫网站-好看的韩国漫画-漫画韩漫网址-韩漫免费阅读漫画',
          'description': '韩漫网站提供好看韩国漫画免费阅读，精选优质韩漫作品和流行题材',
          'url': 'https://www.manhwa-site.com/',
          'isPartOf': { '@type': 'WebSite', 'name': '韩漫网站', 'url': 'https://www.manhwa-site.com' },
        })}} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            { '@type': 'Question', 'name': '韩漫网站上的漫画都是免费的吗？', 'acceptedAnswer': { '@type': 'Answer', 'text': '是的，我们网站上的所有韩国漫画都可以免费阅读，无需付费订阅。我们致力于为广大漫画爱好者提供优质的免费韩漫资源。' } },
            { '@type': 'Question', 'name': '如何在韩漫网站上找到好看的韩国漫画？', 'acceptedAnswer': { '@type': 'Answer', 'text': '您可以通过首页的分类导航浏览不同题材的韩漫，也可以使用搜索功能查找特定作品。热门排行和高分好评板块也能帮助您发现优质漫画。' } },
            { '@type': 'Question', 'name': '韩漫网站的漫画更新频率如何？', 'acceptedAnswer': { '@type': 'Answer', 'text': '我们会及时跟进韩国漫画的最新更新，热门连载作品通常每周都会更新新的章节，确保读者能够第一时间阅读到最新内容。' } },
            { '@type': 'Question', 'name': '韩漫网站支持手机阅读吗？', 'acceptedAnswer': { '@type': 'Answer', 'text': '当然支持！我们的网站采用响应式设计，无论在电脑、平板还是手机上都能获得舒适流畅的阅读体验。' } },
          ],
        })}} />
      </main>

      <Footer />
    </>
  );
}